import React, { useCallback, useEffect, useState } from "react";
import { Download, FileText, Trash2, Upload } from "lucide-react";
import DiyLayout from "../../components/Layout/DiyLayout";
import { useDiySelfClient } from "../../hooks/useDiySelfClient";
import {
  documentsApi,
  DOCUMENT_CATEGORY_LABELS,
  validateDocumentFile,
  type ClientDocument,
} from "../../api/documentsApi";
import { aiApi, getAiApiErrorMessage, type CreditReportParseResultDto } from "../../api/aiApi";
import { CreditReportParseModal } from "../../components/client/CreditReportParseModal";
import { AlertModal } from "../../components/shared/AlertModal";
import { TableSkeletonRows } from "../../components/ui/Skeleton";
import { ScrollReveal } from "../../components/diy/ScrollReveal";
import "../../styles/diy-animations.css";

const DIY_CATEGORIES = ["CreditReport", "ID", "ProofOfAddress", "Other"] as const;

const DiyDocumentsPage: React.FC = () => {
  const { clientId, loading: clientLoading, error: clientError, reload: reloadClient } = useDiySelfClient();
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadCategory, setUploadCategory] = useState<string>("CreditReport");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; fileName: string } | null>(null);
  const [parseModalOpen, setParseModalOpen] = useState(false);
  const [parseLoadingDocId, setParseLoadingDocId] = useState<string | null>(null);
  const [parseView, setParseView] = useState<CreditReportParseResultDto | null>(null);
  const [parseApplying, setParseApplying] = useState(false);
  const [parseModalError, setParseModalError] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const loadDocuments = useCallback(async () => {
    if (!clientId) {
      setDocuments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const list = await documentsApi.list(clientId);
      setDocuments(list);
    } catch {
      setError("Could not load documents.");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  const uploadFiles = async (files: FileList | File[] | null) => {
    if (!clientId || !files?.length) {
      return;
    }
    const file = files[0];
    const validation = validateDocumentFile(file);
    if (validation) {
      setError(validation);
      return;
    }
    setUploading(true);
    setError("");
    try {
      await documentsApi.upload(clientId, file, uploadCategory, uploadDescription);
      setUploadDescription("");
      await loadDocuments();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleParse = async (documentId: string) => {
    setParseLoadingDocId(documentId);
    setError("");
    try {
      const res = await aiApi.parseCreditReport(documentId);
      const parsed = res.parsed;
      setParseView({
        ...parsed,
        tradelines: (parsed.tradelines ?? []).map((t) => ({
          ...t,
          bureaus: t.bureaus ?? [],
          selected: t.selected !== false,
        })),
        inquiries: parsed.inquiries ?? [],
        summary: parsed.summary ?? null,
      });
      setParseModalError("");
      setParseModalOpen(true);
    } catch (err) {
      setError(getAiApiErrorMessage(err, "Could not parse credit report."));
    } finally {
      setParseLoadingDocId(null);
    }
  };

  const handleApply = async () => {
    if (!clientId || !parseView) {
      return;
    }
    setParseApplying(true);
    setParseModalError("");
    try {
      const result = await aiApi.applyParsedReport({
        clientId,
        scores: parseView.scores ?? undefined,
        tradelines: parseView.tradelines?.filter((t) => t.isNegative),
      });
      setParseModalOpen(false);
      setParseView(null);
      await loadDocuments();
      setSuccessMsg(result.message);
      setSuccessOpen(true);
    } catch (err) {
      setParseModalError(getAiApiErrorMessage(err, "Could not apply parsed report."));
    } finally {
      setParseApplying(false);
    }
  };

  const confirmDelete = async () => {
    const t = deleteTarget;
    setDeleteTarget(null);
    if (!t) {
      return;
    }
    try {
      await documentsApi.remove(t.id);
      await loadDocuments();
    } catch {
      setError("Could not delete document.");
    }
  };

  const isPdf = (name: string) => name.toLowerCase().endsWith(".pdf");

  const glass = {
    background: "rgba(255, 255, 255, 0.03)",
    border: "1px solid rgba(255, 255, 255, 0.07)",
  };

  return (
    <DiyLayout>
      <div className="mx-auto max-w-lg md:max-w-2xl">
        <header className="mb-6">
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 22, color: "#F1F5F9" }}>My Documents</h1>
          <p className="mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#94A3B8" }}>
            Upload and manage your credit reports
          </p>
        </header>

        {clientError ? (
          <p className="text-sm" style={{ color: "#FECACA", fontFamily: "'DM Sans', sans-serif" }}>
            {clientError}{" "}
            <button type="button" className="underline" style={{ color: "#0EA5E9" }} onClick={() => void reloadClient()}>
              Retry
            </button>
          </p>
        ) : null}

        <ScrollReveal>
          <section className="rounded-2xl p-5" style={glass}>
            <label className="block text-xs font-medium" style={{ color: "#94A3B8", fontFamily: "'DM Sans', sans-serif" }}>
              Category
            </label>
            <select
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value)}
              className="mt-1 w-full max-w-md rounded-xl border px-3 py-2 text-sm text-white"
              style={{
                borderColor: "rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.04)",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {DIY_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {DOCUMENT_CATEGORY_LABELS[c as keyof typeof DOCUMENT_CATEGORY_LABELS] ?? c}
                </option>
              ))}
            </select>
            <label className="mt-4 block text-xs font-medium" style={{ color: "#94A3B8", fontFamily: "'DM Sans', sans-serif" }}>
              Description (optional)
            </label>
            <input
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2 text-sm text-white"
              style={{
                borderColor: "rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.04)",
                fontFamily: "'DM Sans', sans-serif",
              }}
            />
            <div
              role="button"
              tabIndex={0}
              onDragEnter={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                void uploadFiles(e.dataTransfer.files);
              }}
              className={`mt-4 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-all duration-200 ${
                dragActive ? "animate-pulse" : ""
              }`}
              style={{
                borderColor: dragActive ? "#10B981" : "rgba(16,185,129,0.3)",
                background: dragActive ? "rgba(16,185,129,0.08)" : "rgba(16,185,129,0.03)",
              }}
              onClick={() => document.getElementById("diy-doc-input")?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  document.getElementById("diy-doc-input")?.click();
                }
              }}
            >
              <Upload className="animate-float h-12 w-12" strokeWidth={1.5} color="#10B981" aria-hidden />
              <p className="mt-4" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 16, color: "#E2E8F0" }}>
                Drop your credit report PDF here
              </p>
              <p className="mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#94A3B8" }}>
                or tap to browse files
              </p>
              <p className="mt-2" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#64748B" }}>
                PDF files up to 10MB · PDF, images, Word supported
              </p>
              <input
                id="diy-doc-input"
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => void uploadFiles(e.target.files)}
              />
            </div>
            {uploading ? (
              <p className="mt-2 text-sm" style={{ color: "#10B981", fontFamily: "'DM Sans', sans-serif" }}>
                Uploading…
              </p>
            ) : null}
            {error ? (
              <p className="mt-2 text-sm" style={{ color: "#FECACA", fontFamily: "'DM Sans', sans-serif" }}>
                {error}
              </p>
            ) : null}
          </section>
        </ScrollReveal>

        <section className="mt-10">
          <p
            className="mb-4"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 600,
              fontSize: 10,
              color: "#10B981",
              letterSpacing: 2,
            }}
          >
            UPLOADED FILES
          </p>
          {loading || clientLoading ? (
            <TableSkeletonRows rows={4} cols={2} />
          ) : documents.length === 0 ? (
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#64748B" }}>No documents yet.</p>
          ) : (
            <ul className="space-y-3">
              {documents.map((d, i) => (
                <ScrollReveal key={d.id} delay={i * 0.05}>
                  <li
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl px-4 py-3"
                    style={glass}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                        style={{ background: "rgba(239,68,68,0.1)" }}
                      >
                        <FileText className="h-4 w-4" strokeWidth={2} color="#EF4444" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-white" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
                          {d.fileName}
                        </p>
                        <p className="text-[11px]" style={{ fontFamily: "'DM Sans', sans-serif", color: "#64748B" }}>
                          {new Date(d.createdAt).toLocaleString()} · {(d.fileSize / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {d.category === "CreditReport" && isPdf(d.fileName) ? (
                        <button
                          type="button"
                          onClick={() => void handleParse(d.id)}
                          disabled={parseLoadingDocId === d.id}
                          className="cursor-pointer rounded-lg border-0 px-3 py-2 text-xs font-semibold text-white disabled:opacity-40"
                          style={{
                            fontFamily: "'DM Sans', sans-serif",
                            background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                          }}
                        >
                          {parseLoadingDocId === d.id ? "Parsing…" : "Parse with AI"}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => void documentsApi.download(d.id, d.fileName)}
                        className="flex h-7 w-7 items-center justify-center rounded-lg border-0 transition-colors hover:bg-white/10"
                        style={{ background: "rgba(255,255,255,0.06)", color: "#94A3B8" }}
                        aria-label="Download"
                      >
                        <Download className="h-4 w-4" strokeWidth={2} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget({ id: d.id, fileName: d.fileName })}
                        className="flex h-7 w-7 items-center justify-center rounded-lg border-0 transition-colors hover:bg-white/10"
                        style={{ background: "rgba(255,255,255,0.06)", color: "#94A3B8" }}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={2} />
                      </button>
                    </div>
                  </li>
                </ScrollReveal>
              ))}
            </ul>
          )}
        </section>
      </div>

      <CreditReportParseModal
        open={parseModalOpen}
        onClose={() => {
          setParseModalOpen(false);
          setParseView(null);
          setParseModalError("");
        }}
        parseResult={parseView}
        onChangeTradelines={(next) => setParseView((prev) => (prev ? { ...prev, tradelines: next } : prev))}
        onApply={() => void handleApply()}
        applying={parseApplying}
        error={parseModalError}
      />

      <AlertModal
        isOpen={successOpen}
        title="Applied"
        message={successMsg}
        variant="success"
        buttonLabel="OK"
        onClose={() => {
          setSuccessOpen(false);
          setSuccessMsg("");
        }}
      />

      {deleteTarget ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6">
            <h3 className="text-lg font-semibold text-white">Delete document?</h3>
            <p className="mt-2 text-sm text-slate-400">{deleteTarget.fileName}</p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-300"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white"
                onClick={() => void confirmDelete()}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </DiyLayout>
  );
};

export default DiyDocumentsPage;
